import React, { FC, useEffect } from 'react';
import {
  ILongContractRequestResponse,
  IPharmacistDetails,
  PaymentPlans,
  PharmacyLegendVariant,
  TypeOfBill,
  TypeOfSummary,
  getHourlyRate,
  pharmacyActions,
  pharmacyBillingSelector,
} from '@pharmaplan/common';
import { Typography } from '@mui/material';

import useSelfService from '../../../hooks/useSelfService';
import usePharmacyPreRequest from '../../../hooks/PharmacistProfile/usePharmacyPreRequest';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { Constants, ScreenTypes } from '../../../helpers/Constants';
import strings from '../../../localization';

import { IBookingRequestsList } from '../BookingRequestsList/BookingRequestsList';
import useCheckBookingStatus from '../../../hooks/Payment/useCheckBookingStatus';
import PharmacistSummary from '../PharmacistSummary/PharmacistSummary';
import SoftwareExpertise from '../SoftwareExpertise/SoftwareExpertise';
import TotalContractValue from '../../common/TotalContractValue';
import PharmacistCard from '../PharmacistCard/PharmacistCard';
import NoteLabelValue from '../../common/NoteLabelValue';
import LongTermBillDetails from '../LongTermBillDetails';
import ServiceNote from '../ServiceNotes/ServiceNotes';
import BillDetails from '../BillDetails/BillDetails';
import CustomButton from '../../common/CustomButton';
import ServiceCard from '../ServiceCard';

import LongShiftsDistribution from './LongShiftsDistribution';
import { InfoNotesType } from './InfoNote/InfoNote';
import BillableCard from './BillableCard';
import LongTermNote from './LongTermNote';
import InfoNote from './InfoNote';
import classes from './styles';
import { useAppDispatch } from '../../../hooks/useAppDispatch';

export interface IPharmacistProfile extends IBookingRequestsList {
  workshiftId: string;
  longTermContractId?: string;
  professionalId?: string;
  preventInteraction?: boolean;
}

const { bill, counterOffer } = TypeOfBill;
const { pharmacistProfile } = ScreenTypes;
const { workshiftDetails } = TypeOfSummary;

const {
  getBookedPharmacistDetails: getBookedDetailsAction,
  getRequestedLongContractDetails,
} = pharmacyActions;

const PharmacistProfile: FC<IPharmacistProfile> = ({
  eventId,
  type,
  eventIds,
  date,
  workshiftId,
  longTermContractId,
  preventInteraction,
  professionalId,
}) => {
  const dispatch = useAppDispatch();
  const { isSelfService } = useSelfService();
  const {
    showButtonsWhen,
    isCounterOffer,
    dateTime,
    details,
    goToBookings,
    comeBackFromRating,
  } = usePharmacyPreRequest({
    eventId,
    eventIds,
    type,
    date,
    workshiftId,
    preventInteraction,
    longTermContractId,
    professionalId,
  });

  const isLongTerm = !!longTermContractId && type === PharmacyLegendVariant.requested;
  const successAction = isLongTerm
    ? getRequestedLongContractDetails
    : getBookedDetailsAction;

  const billing = useAppSelector(pharmacyBillingSelector);

  useCheckBookingStatus(
    (details as ILongContractRequestResponse)?.bookingId ?? eventId,
  );

  const { currentPlan } = billing ?? {};
  const isBasic = currentPlan?.toLowerCase() === PaymentPlans.basic;

  const {
    pharmaPlanPercentFees,
    mealAllowance,
    paidHourlyRate,
    softwares,
    counterNote,
    accommodationAllowance,
    externalPharmacistId,
  } = (details as IPharmacistDetails) ?? {};

  const {
    longTermShiftContract,
    requests,
    professionalBookedCount,
    counterShiftContract,
    contractCost,
  } = (details as ILongContractRequestResponse) ?? {};

  const {
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
    allowanceNotes,
    longTermShiftContractId: isBookedTerm,
  } = longTermShiftContract ?? {};
  const {
    weekDayPaidHourlyRate: counterWeekdayRate,
    weekendPaidHourlyRate: counterWeekendRate,
    allowanceNotes: proposedAllowance,
  } = counterShiftContract ?? {};

  const billType = showButtonsWhen ? bill : counterOffer;
  const isRequested = type === PharmacyLegendVariant.requested;

  const parsedEvent = {
    ...details,
    fixedMealRate: mealAllowance,
    paidHourlyRate,
    fixedAccommodationRate: accommodationAllowance,
  };

  const isBooked = type === PharmacyLegendVariant.booked;
  const billEvent = details;
  const { counterOfferTitle } = classes;

  useEffect(() => {
    dispatch(getHourlyRate());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <PharmacistCard
        goBack={comeBackFromRating}
        successAction={successAction}
        eventId={eventId}
        pharmacistDetails={details as IPharmacistDetails}
        type={type}
        date={date}
        workshiftId={workshiftId}
        eventIds={eventIds}
        screenType={pharmacistProfile}
        isExternalPharmacist={!!externalPharmacistId}
        customFooterEnd={
          (details as IPharmacistDetails)?.longTermShiftContractId ? (
            <CustomButton
              customButtonStyle={classes.counterOfferBtn}
              customClass={classes.counterOfferContainer}
              onClick={goToBookings}
              label={strings.viewAllBookings}
              variant={Constants.variant.outlined}
            />
          ) : null
        }
        showRating
      />

      <SoftwareExpertise
        successAction={successAction}
        softwareDetails={softwares}
      />

      {isLongTerm ? (
        <>
          <LongShiftsDistribution
            details={details as ILongContractRequestResponse}
            counterWeekdayRate={counterWeekdayRate}
            counterWeekendRate={counterWeekendRate}
            weekdayHourlyRate={weekDayPaidHourlyRate}
            weekendHourlyRate={weekendPaidHourlyRate}
            successAction={successAction}
          />

          <LongTermNote />

          {!!allowanceNotes && (
            <ServiceCard
              customContainerStyle={classes.padTop}
              content={(
                <NoteLabelValue
                  label={strings.allowances}
                  value={allowanceNotes}
                />
              )}
            />
          )}

          {!!proposedAllowance && (
            <ServiceCard
              customContainerStyle={classes.padTop}
              content={(
                <NoteLabelValue
                  label={strings.proposedAllowances}
                  value={proposedAllowance}
                />
              )}
            />
          )}

          <LongTermBillDetails
            details={details as ILongContractRequestResponse}
            successAction={successAction}
          />
        </>
      ) : (
        <>
          {isCounterOffer && (
            <Typography sx={counterOfferTitle}>
              {strings.counterOffer}
            </Typography>
          )}
          <PharmacistSummary
            event={parsedEvent as IPharmacistDetails}
            summaryType={workshiftDetails}
            isCounterOffer={!!isCounterOffer}
            type={type}
            isLongTerm={
              !!(details as IPharmacistDetails)?.longTermShiftContractId
            }
            DateTime={dateTime}
          />

          {!!allowanceNotes && isBooked && (
            <ServiceCard
              customContainerStyle={classes.padTop}
              content={(
                <NoteLabelValue
                  label={strings.allowances}
                  value={allowanceNotes}
                />
              )}
            />
          )}

          <BillDetails
            successAction={successAction}
            billType={billType}
            event={billEvent as IPharmacistDetails}
            isCounterOffer={!!isCounterOffer}
            percentFees={parseInt(pharmaPlanPercentFees ?? '', 10)}
          />

          {showButtonsWhen && counterNote && (
            <ServiceNote notes={counterNote} />
          )}
        </>
      )}

      {isSelfService && <InfoNote type={InfoNotesType.applicableTaxes} />}

      {isSelfService && isBasic && isRequested && (
        <BillableCard requestedShifts={requests?.length} />
      )}

      {isBookedTerm && isBooked && (
        <TotalContractValue
          days={professionalBookedCount ?? 0}
          value={contractCost ?? 0}
        />
      )}

      {isSelfService && isBasic && isRequested && (
        <InfoNote type={InfoNotesType.onlyPharmaGapFees} />
      )}
    </>
  );
};

export default PharmacistProfile;
